import { Controller } from "@hotwired/stimulus";

const GAP = 8;

// Shared anchoring engine for hover/tap popovers (tooltip, info-tooltip).
// Subclasses provide their own identifier + SCSS; behavior lives here.
export default class extends Controller {
  static targets = ["trigger", "bubble"];
  static values = { side: { type: String, default: "top" } };

  connect() {
    // Progressive enhancement: the bubble ships with `hidden` so it is
    // invisible without JS. Once the controller is live, hand visibility
    // over to CSS (visibility/opacity) so the bubble stays measurable.
    this.bubbleTarget.hidden = false;
    this.open = false;

    this.reposition = this.position.bind(this);
    this.onOutsideClick = this.handleOutsideClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
  }

  disconnect() {
    this.teardownListeners();
  }

  show() {
    if (this.open) return;

    this.open = true;
    this.position();
    // Toggle a data attribute rather than a class: scoped PostCSS rewrites
    // class selectors to `c-*`, but leaves attribute selectors alone.
    this.bubbleTarget.dataset.open = "";

    document.addEventListener("click", this.onOutsideClick);
    document.addEventListener("keydown", this.onKeydown);
    window.addEventListener("scroll", this.reposition, { passive: true });
    window.addEventListener("resize", this.reposition);
  }

  hide() {
    if (!this.open) return;

    this.open = false;
    delete this.bubbleTarget.dataset.open;
    this.teardownListeners();
  }

  toggle(event) {
    // The tap that toggles must not also bubble up as an outside click.
    event.preventDefault();
    this.open ? this.hide() : this.show();
  }

  position() {
    const trigger = this.triggerTarget.getBoundingClientRect();
    const width = this.bubbleTarget.offsetWidth;
    const height = this.bubbleTarget.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let side = this.sideValue;

    // Auto-flip to the opposite side when the preferred side has no room.
    if (side === "top" && trigger.top - height - GAP < 0 && trigger.bottom + height + GAP <= vh) {
      side = "bottom";
    } else if (side === "bottom" && trigger.bottom + height + GAP > vh && trigger.top - height - GAP >= 0) {
      side = "top";
    } else if (side === "left" && trigger.left - width - GAP < 0 && trigger.right + width + GAP <= vw) {
      side = "right";
    } else if (side === "right" && trigger.right + width + GAP > vw && trigger.left - width - GAP >= 0) {
      side = "left";
    }

    const triggerCenterX = trigger.left + trigger.width / 2;
    const triggerCenterY = trigger.top + trigger.height / 2;

    let top, left;
    if (side === "top" || side === "bottom") {
      top = side === "top" ? trigger.top - height - GAP : trigger.bottom + GAP;
      left = this.clamp(triggerCenterX - width / 2, GAP, vw - width - GAP);
      // Keep the caret pointing at the trigger after the bubble is clamped.
      this.bubbleTarget.style.setProperty("--caret-pos", `${this.clamp(triggerCenterX - left, GAP, width - GAP)}px`);
    } else {
      left = side === "left" ? trigger.left - width - GAP : trigger.right + GAP;
      top = this.clamp(triggerCenterY - height / 2, GAP, vh - height - GAP);
      this.bubbleTarget.style.setProperty("--caret-pos", `${this.clamp(triggerCenterY - top, GAP, height - GAP)}px`);
    }

    this.bubbleTarget.style.top = `${top}px`;
    this.bubbleTarget.style.left = `${left}px`;
    this.bubbleTarget.dataset.side = side;
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) this.hide();
  }

  handleKeydown(event) {
    if (event.key === "Escape") this.hide();
  }

  teardownListeners() {
    document.removeEventListener("click", this.onOutsideClick);
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("scroll", this.reposition);
    window.removeEventListener("resize", this.reposition);
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
}
